import { Box } from '@palmetto/palmetto-components';

/**
 * Visual connector linking a parent quote/contract row to its indented change-order child row.
 * Shared by the Quotes and Contracts lists so both use identical geometry and colors.
 */

// Badge border color tokens — match the badge each connector points at, and follow light/dark mode.
export const SUCCESS_CONNECTOR_COLOR = 'var(--color-border-badge-success)'; // active/approved
export const INFO_CONNECTOR_COLOR = 'var(--color-border-badge-info)'; // changeOrderPending/created/sent
export const WARNING_CONNECTOR_COLOR = 'var(--color-border-badge-warning)'; // voided/cancelled

// Horizontal geometry, measured from each row's left edge (before the row's own padding):
// - Row padding-left is `lg` (24px), so the parent badge's left edge sits at 24px.
// - The child row is indented by `2xl` (40px), so the child badge's left edge sits at 64px.
//   CHILD_INDENT must match the `2xl` margin token the list applies to the child badge box.
// The line/arc sit at CONNECTOR_LEFT — inset from the parent badge's left edge so the line runs
// under the label rather than flush with its front — and the arc bridges ARC_WIDTH px across to
// meet the child badge's left edge.
const ROW_PADDING_LEFT = 24;
export const CHILD_INDENT = 40;
const CONNECTOR_INSET = 10;
const CONNECTOR_LEFT = ROW_PADDING_LEFT + CONNECTOR_INSET;
const ARC_WIDTH = ROW_PADDING_LEFT + CHILD_INDENT - CONNECTOR_LEFT;

/**
 * Vertical line dropping from the bottom of the parent badge through the rest of the parent row.
 * Render inside a `position: relative` wrapper around the parent row. `top` should clear the
 * parent badge (its value depends on the row's top padding).
 */
export function ConnectorParentLine({ color, top }: { color: string; top: number }): React.JSX.Element {
  return (
    <Box
      style={{
        position: 'absolute',
        left: CONNECTOR_LEFT,
        top,
        bottom: 0,
        width: 2,
        background: color,
        pointerEvents: 'none',
        zIndex: 1,
      }}
    />
  );
}

/**
 * └ arc: the vertical leg continues the parent line, the horizontal leg runs right to meet the
 * indented child badge's left edge. Render inside a `position: relative` wrapper around the child
 * row. `height` lands the horizontal leg on the child badge's vertical center.
 */
export function ConnectorChildArc({ color, height }: { color: string; height: number }): React.JSX.Element {
  return (
    <Box
      style={{
        position: 'absolute',
        left: CONNECTOR_LEFT,
        top: 0,
        width: ARC_WIDTH,
        height,
        borderLeft: `2px solid ${color}`,
        borderBottom: `2px solid ${color}`,
        borderBottomLeftRadius: 16,
        pointerEvents: 'none',
        zIndex: 1,
      }}
    />
  );
}
